import {
  mergeMap as _observableMergeMap,
  catchError as _observableCatch,
} from "rxjs/operators";
import {
  Observable,
  throwError as _observableThrow,
  of as _observableOf,
} from "rxjs";
import { Injectable, Inject, Optional } from "@angular/core";
import {
  HttpClient,
  HttpHeaders,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";

import {
  API_BASE_URL,
  FlatPermissionDto,
  PermissionDtoListResultDto,
} from "./service-proxies";

@Injectable()
export class DocumentsServiceProxy {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string
  ) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
  }

  getAllUserDocuments(
    keyword: string | undefined,
    skipCount: number | undefined,
    maxResultCount: number | undefined,
    isInOffice: boolean | undefined,
    userId: number | undefined = null,
  ): Observable<UserDocumentPagedResultDto> {
    let url_ = this.baseUrl + "/api/services/app/UserDocument/GetAll?";
    if (keyword === null)
      throw new Error("The parameter 'keyword' cannot be null.");
    else if (keyword !== undefined)
      url_ += "Keyword=" + encodeURIComponent("" + keyword) + "&";
    if (isInOffice === null)
      throw new Error("The parameter 'isInOffice' cannot be null.");
    else if (isInOffice !== undefined)
      url_ += "IsInOffice=" + encodeURIComponent("" + isInOffice) + "&";
    if (skipCount === null)
      throw new Error("The parameter 'skipCount' cannot be null.");
    else if (skipCount !== undefined)
      url_ += "SkipCount=" + encodeURIComponent("" + skipCount) + "&";
    if (maxResultCount === null)
      throw new Error("The parameter 'maxResultCount' cannot be null.");
    else if (maxResultCount !== undefined)
      url_ += "MaxResultCount=" + encodeURIComponent("" + maxResultCount) + "&";
    if (userId != null)
      url_ += "UserId=" + encodeURIComponent("" + userId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAll(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAll(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<UserDocumentPagedResultDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<UserDocumentPagedResultDto>;
        })
      );
  }

  getAllLinkeedUserDocuments(
    UserDocumentId: number | undefined = null,
  ): Observable<UserDocumentPagedResultDto> {
    let url_ = this.baseUrl + "/api/services/app/UserDocument/GetAllLinked?";
    if (UserDocumentId === null)
      throw new Error("The parameter 'UserDocumentId' cannot be null.");
    else if (UserDocumentId !== undefined)
      url_ += "UserDocumentId=" + encodeURIComponent("" + UserDocumentId) + "&";

    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAll(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAll(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<UserDocumentPagedResultDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<UserDocumentPagedResultDto>;
        })
      );
  }

  protected processGetAll(
    response: HttpResponseBase
  ): Observable<UserDocumentPagedResultDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = UserDocumentPagedResultDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  AddUserDocument(
    body: CreateUserDocumentDto | undefined
  ): Observable<CreateUserDocumentResultDto> {
    let url_ = this.baseUrl + "/api/services/app/UserDocument/Create";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processCreate(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processCreate(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<CreateUserDocumentResultDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<CreateUserDocumentResultDto>;
        })
      );
  }

  delete(id: number | undefined): Observable<void> {
    let url_ = this.baseUrl + "/api/services/app/UserDocument/Delete?";
    if (id === null)
        throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
        url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
        observe: "response",
        responseType: "blob",
        headers: new HttpHeaders({
        })
    };

    return this.http.request("delete", url_, options_).pipe(_observableMergeMap((response_: any) => {
        return this.processDelete(response_);
    })).pipe(_observableCatch((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
            try {
                return this.processDelete(response_ as any);
            } catch (e) {
                return _observableThrow(e) as any as Observable<void>;
            }
        } else
            return _observableThrow(response_) as any as Observable<void>;
    }));
}

protected processDelete(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
        response instanceof HttpResponse ? response.body :
            (response as any).error instanceof Blob ? (response as any).error : undefined;

    let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); } }
    if (status === 200) {
        return blobToText(responseBlob).pipe(_observableMergeMap((_responseText: string) => {
            return _observableOf(null as any);
        }));
    } else if (status !== 200 && status !== 204) {
        return blobToText(responseBlob).pipe(_observableMergeMap((_responseText: string) => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
        }));
    }
    return _observableOf(null as any);
}

  protected processCreate(
    response: HttpResponseBase
  ): Observable<CreateUserDocumentResultDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = CreateUserDocumentResultDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  SendUserDocument(body: SendUserDocumentDto | undefined): Observable<boolean> {
    let url_ = this.baseUrl + "/api/services/app/UserDocument/SendDocument";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processSendReturnDocument(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processSendReturnDocument(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<boolean>;
            }
          } else
            return _observableThrow(response_) as any as Observable<boolean>;
        })
      );
  }

  ReturnUserDocument(
    body: ReturnUserDocumentDto | undefined
  ): Observable<boolean> {
    let url_ = this.baseUrl + "/api/services/app/UserDocument/ReturnDocument";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processSendReturnDocument(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processSendReturnDocument(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<boolean>;
            }
          } else
            return _observableThrow(response_) as any as Observable<boolean>;
        })
      );
  }

  protected processSendReturnDocument(
    response: HttpResponseBase
  ): Observable<boolean> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200;
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  getUserDocumentTransitionLogs(
    userDoucmentId: number
  ): Observable<UserDocumentTransitionLogResultDto> {
    let url_ =
      this.baseUrl +
      "/api/services/app/UserDocument/GetUserDocumentTransitionLog?";
    if (userDoucmentId === null)
      throw new Error("The parameter 'keyword' cannot be null.");
    else if (userDoucmentId !== undefined)
      url_ += "userDocumentId=" + encodeURIComponent("" + userDoucmentId) + "&";

    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetUserDocumentTransitionLogs(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetUserDocumentTransitionLogs(
                response_ as any
              );
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<UserDocumentTransitionLogResultDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<UserDocumentTransitionLogResultDto>;
        })
      );
  }

  protected processGetUserDocumentTransitionLogs(
    response: HttpResponseBase
  ): Observable<UserDocumentTransitionLogResultDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = UserDocumentTransitionLogResultDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  protected processGetDocuumentTransitionFiles(
    response: HttpResponseBase
  ): Observable<DocumentFileModel[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200;
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }
  getDocuumentTransitionFiles(
    documentTransitionLogId: number
  ): Observable<DocumentFileModel[]> {
    let url_ =
      this.baseUrl +
      "/api/services/app/UserDocument/getDocuumentTransitionFiles?";
    if (documentTransitionLogId === null) {
      throw new Error("The parameter 'ticketId' cannot be null.");
    } else if (documentTransitionLogId !== undefined) {
      url_ +=
        "userDocumentTransitionId=" +
        encodeURIComponent("" + documentTransitionLogId) +
        "&";
    }
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetDocuumentTransitionFiles(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetDocuumentTransitionFiles(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<
                DocumentFileModel[]
              >;
            }
          } else
            return _observableThrow(response_) as any as Observable<
              DocumentFileModel[]
            >;
        })
      );
  }
}

@Injectable()
export class DocumentSendReasonServiceProxy {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string
  ) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
  }

  /**
   * @param body (optional)
   * @return Success
   */
  create(
    body: DocumentSendReasonDto | undefined
  ): Observable<DocumentSendReasonDto> {
    let url_ = this.baseUrl + "/api/services/app/DocumentSendReason/Create";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processCreate(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processCreate(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<DocumentSendReasonDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<DocumentSendReasonDto>;
        })
      );
  }

  protected processCreate(
    response: HttpResponseBase
  ): Observable<DocumentSendReasonDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = DocumentSendReasonDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param id (optional)
   * @return Success
   */
  delete(id: number | undefined): Observable<void> {
    let url_ = this.baseUrl + "/api/services/app/DocumentSendReason/Delete?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({}),
    };

    return this.http
      .request("delete", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processDelete(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processDelete(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        })
      );
  }

  protected processDelete(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param id (optional)
   * @return Success
   */
  get(id: number | undefined): Observable<DocumentSendReasonDto> {
    let url_ = this.baseUrl + "/api/services/app/DocumentSendReason/Get?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGet(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGet(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<DocumentSendReasonDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<DocumentSendReasonDto>;
        })
      );
  }

  protected processGet(
    response: HttpResponseBase
  ): Observable<DocumentSendReasonDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = DocumentSendReasonDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param keyword (optional)
   * @param isActive (optional)
   * @param skipCount (optional)
   * @param maxResultCount (optional)
   * @return Success
   */
  getAll(
    keyword: string | undefined,
    isActive: boolean | undefined,
    skipCount: number | undefined,
    maxResultCount: number | undefined
  ): Observable<DocumentSendReasonDtoPagedResultDto> {
    let url_ = this.baseUrl + "/api/services/app/DocumentSendReason/GetAll?";
    if (keyword === null)
      throw new Error("The parameter 'keyword' cannot be null.");
    else if (keyword !== undefined)
      url_ += "Keyword=" + encodeURIComponent("" + keyword) + "&";
    if (isActive === null)
      throw new Error("The parameter 'isActive' cannot be null.");
    else if (isActive !== undefined)
      url_ += "IsActive=" + encodeURIComponent("" + isActive) + "&";
    if (skipCount === null)
      throw new Error("The parameter 'skipCount' cannot be null.");
    else if (skipCount !== undefined)
      url_ += "SkipCount=" + encodeURIComponent("" + skipCount) + "&";
    if (maxResultCount === null)
      throw new Error("The parameter 'maxResultCount' cannot be null.");
    else if (maxResultCount !== undefined)
      url_ += "MaxResultCount=" + encodeURIComponent("" + maxResultCount) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAll(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAll(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<DocumentSendReasonDtoPagedResultDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<DocumentSendReasonDtoPagedResultDto>;
        })
      );
  }

  protected processGetAll(
    response: HttpResponseBase
  ): Observable<DocumentSendReasonDtoPagedResultDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = DocumentSendReasonDtoPagedResultDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param body (optional)
   * @return Success
   */
  update(
    body: DocumentSendReasonDto | undefined
  ): Observable<DocumentSendReasonDto> {
    let url_ = this.baseUrl + "/api/services/app/DocumentSendReason/Update";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("put", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processUpdate(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processUpdate(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<DocumentSendReasonDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<DocumentSendReasonDto>;
        })
      );
  }

  protected processUpdate(
    response: HttpResponseBase
  ): Observable<DocumentSendReasonDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = DocumentSendReasonDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }
}

export class DocumentSendReasonDto implements IDocumentSendReasonDto {
  id: number;
  code: number;
  name: string;

  constructor(data?: IDocumentSendReasonDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.code = _data["code"];
      this.name = _data["name"];
    }
  }

  static fromJS(data: any): DocumentSendReasonDto {
    data = typeof data === "object" ? data : {};
    let result = new DocumentSendReasonDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["code"] = this.code;
    data["name"] = this.name;
    return data;
  }

  clone(): DocumentSendReasonDto {
    const json = this.toJSON();
    let result = new DocumentSendReasonDto();
    result.init(json);
    return result;
  }
}

export interface IDocumentSendReasonDto {
  id: number;
  code: number;
  name: string;
}

export class DocumentSendReasonDtoPagedResultDto
  implements IDocumentSendReasonDtoPagedResultDto
{
  items: DocumentSendReasonDto[] | undefined;
  totalCount: number;

  constructor(data?: IDocumentSendReasonDtoPagedResultDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["items"])) {
        this.items = [] as any;
        for (let item of _data["items"])
          this.items.push(DocumentSendReasonDto.fromJS(item));
      }
      this.totalCount = _data["totalCount"];
    }
  }

  static fromJS(data: any): DocumentSendReasonDtoPagedResultDto {
    data = typeof data === "object" ? data : {};
    let result = new DocumentSendReasonDtoPagedResultDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.items)) {
      data["items"] = [];
      for (let item of this.items) data["items"].push(item.toJSON());
    }
    data["totalCount"] = this.totalCount;
    return data;
  }

  clone(): DocumentSendReasonDtoPagedResultDto {
    const json = this.toJSON();
    let result = new DocumentSendReasonDtoPagedResultDto();
    result.init(json);
    return result;
  }
}

export interface IDocumentSendReasonDtoPagedResultDto {
  items: DocumentSendReasonDto[] | undefined;
  totalCount: number;
}

@Injectable()
export class DocumentLocationServiceProxy {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string
  ) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
  }

  /**
   * @param body (optional)
   * @return Success
   */
  create(
    body: DocumentLocationDto | undefined
  ): Observable<DocumentLocationDto> {
    let url_ = this.baseUrl + "/api/services/app/DocumentLocation/Create";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processCreate(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processCreate(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<DocumentLocationDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<DocumentLocationDto>;
        })
      );
  }

  protected processCreate(
    response: HttpResponseBase
  ): Observable<DocumentLocationDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = DocumentLocationDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param id (optional)
   * @return Success
   */
  delete(id: number | undefined): Observable<void> {
    let url_ = this.baseUrl + "/api/services/app/DocumentLocation/Delete?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({}),
    };

    return this.http
      .request("delete", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processDelete(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processDelete(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        })
      );
  }

  protected processDelete(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param id (optional)
   * @return Success
   */
  get(id: number | undefined): Observable<DocumentLocationDto> {
    let url_ = this.baseUrl + "/api/services/app/DocumentLocation/Get?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGet(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGet(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<DocumentLocationDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<DocumentLocationDto>;
        })
      );
  }

  protected processGet(
    response: HttpResponseBase
  ): Observable<DocumentLocationDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = DocumentLocationDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param keyword (optional)
   * @param isActive (optional)
   * @param skipCount (optional)
   * @param maxResultCount (optional)
   * @return Success
   */
  getAll(
    keyword: string | undefined,
    isActive: boolean | undefined,
    skipCount: number | undefined,
    maxResultCount: number | undefined
  ): Observable<DocumentLocationDtoPagedResultDto> {
    let url_ = this.baseUrl + "/api/services/app/DocumentLocation/GetAll?";
    if (keyword === null)
      throw new Error("The parameter 'keyword' cannot be null.");
    else if (keyword !== undefined)
      url_ += "Keyword=" + encodeURIComponent("" + keyword) + "&";
    if (isActive === null)
      throw new Error("The parameter 'isActive' cannot be null.");
    else if (isActive !== undefined)
      url_ += "IsActive=" + encodeURIComponent("" + isActive) + "&";
    if (skipCount === null)
      throw new Error("The parameter 'skipCount' cannot be null.");
    else if (skipCount !== undefined)
      url_ += "SkipCount=" + encodeURIComponent("" + skipCount) + "&";
    if (maxResultCount === null)
      throw new Error("The parameter 'maxResultCount' cannot be null.");
    else if (maxResultCount !== undefined)
      url_ += "MaxResultCount=" + encodeURIComponent("" + maxResultCount) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAll(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAll(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<DocumentLocationDtoPagedResultDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<DocumentLocationDtoPagedResultDto>;
        })
      );
  }

  protected processGetAll(
    response: HttpResponseBase
  ): Observable<DocumentLocationDtoPagedResultDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = DocumentLocationDtoPagedResultDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param body (optional)
   * @return Success
   */
  update(
    body: DocumentLocationDto | undefined
  ): Observable<DocumentLocationDto> {
    let url_ = this.baseUrl + "/api/services/app/DocumentLocation/Update";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("put", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processUpdate(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processUpdate(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<DocumentLocationDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<DocumentLocationDto>;
        })
      );
  }

  protected processUpdate(
    response: HttpResponseBase
  ): Observable<DocumentLocationDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = DocumentLocationDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }
}

export class DocumentLocationDto implements IDocumentLocationDto {
  id: number;
  code: number;
  name: string;
  isInOffice: boolean;

  constructor(data?: IDocumentLocationDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.code = _data["code"];
      this.name = _data["name"];
      this.isInOffice = _data["isInOffice"];
    }
  }

  static fromJS(data: any): DocumentLocationDto {
    data = typeof data === "object" ? data : {};
    let result = new DocumentLocationDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["code"] = this.code;
    data["name"] = this.name;
    data["isInOffice"] = this.isInOffice;
    return data;
  }

  clone(): DocumentLocationDto {
    const json = this.toJSON();
    let result = new DocumentLocationDto();
    result.init(json);
    return result;
  }
}

export interface IDocumentLocationDto {
  id: number;
  code: number;
  name: string;
  isInOffice: boolean;
}

export class DocumentLocationDtoPagedResultDto
  implements IDocumentLocationDtoPagedResultDto
{
  items: DocumentLocationDto[] | undefined;
  totalCount: number;

  constructor(data?: IDocumentLocationDtoPagedResultDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["items"])) {
        this.items = [] as any;
        for (let item of _data["items"])
          this.items.push(DocumentLocationDto.fromJS(item));
      }
      this.totalCount = _data["totalCount"];
    }
  }

  static fromJS(data: any): DocumentLocationDtoPagedResultDto {
    data = typeof data === "object" ? data : {};
    let result = new DocumentLocationDtoPagedResultDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.items)) {
      data["items"] = [];
      for (let item of this.items) data["items"].push(item.toJSON());
    }
    data["totalCount"] = this.totalCount;
    return data;
  }

  clone(): DocumentLocationDtoPagedResultDto {
    const json = this.toJSON();
    let result = new DocumentLocationDtoPagedResultDto();
    result.init(json);
    return result;
  }
}

export interface IDocumentLocationDtoPagedResultDto {
  items: DocumentLocationDto[] | undefined;
  totalCount: number;
}

export class UserDocumentPagedResultDto implements IUserDocumentPagedResultDto {
  items: UserDocumentDto[] | undefined;
  totalCount: number;

  constructor(data?: IUserDocumentPagedResultDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["items"])) {
        this.items = [] as any;
        for (let item of _data["items"])
          this.items.push(UserDocumentDto.fromJS(item));
      }
      this.totalCount = _data["totalCount"];
    }
  }

  static fromJS(data: any): UserDocumentPagedResultDto {
    data = typeof data === "object" ? data : {};
    let result = new UserDocumentPagedResultDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.items)) {
      data["items"] = [];
      for (let item of this.items) data["items"].push(item.toJSON());
    }
    data["totalCount"] = this.totalCount;
    return data;
  }

  clone(): UserDocumentPagedResultDto {
    const json = this.toJSON();
    let result = new UserDocumentPagedResultDto();
    result.init(json);
    return result;
  }
}

export interface IUserDocumentPagedResultDto {
  items: UserDocumentDto[] | undefined;
  totalCount: number;
}

export class UserDocumentTransitionLogResultDto
  implements IUserDocumentTransitionLogResultDto
{
  items: UserDocumentTransitionLogDto[] | undefined;
  totalCount: number;

  constructor(data?: IUserDocumentTransitionLogResultDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["items"])) {
        this.items = [] as any;
        for (let item of _data["items"])
          this.items.push(UserDocumentTransitionLogDto.fromJS(item));
      }
      this.totalCount = _data["totalCount"];
    }
  }

  static fromJS(data: any): UserDocumentTransitionLogResultDto {
    data = typeof data === "object" ? data : {};
    let result = new UserDocumentTransitionLogResultDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.items)) {
      data["items"] = [];
      for (let item of this.items) data["items"].push(item.toJSON());
    }
    data["totalCount"] = this.totalCount;
    return data;
  }

  clone(): UserDocumentTransitionLogResultDto {
    const json = this.toJSON();
    let result = new UserDocumentTransitionLogResultDto();
    result.init(json);
    return result;
  }
}

export interface IUserDocumentTransitionLogResultDto {
  items: UserDocumentTransitionLogDto[] | undefined;
  totalCount: number;
}

export class UserDocumentTransitionLogDto
  implements IUserDocumentTransitionLogDto
{
  id: number;
  userDocumentId: number;
  documentLocationId: string;
  documentLocationName: string;
  isReturned: boolean;
  sendTime: string;
  sendUserId: number;
  sendUserFullName: string;
  expectedReturnTime: string;
  returnUserId: number;
  returnUserFullName: string;
  sendDescription: string;
  returnDescription: string;
  returnTime: string;
  returnDocumentHealthConfirm: boolean;
  healthProblemDescription: string;
  files: DocumentFileModel[] = [];

  constructor(data?: UserDocumentTransitionLogDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.userDocumentId = _data["userDocumentId"];
      this.documentLocationId = _data["documentLocationId"];
      this.documentLocationName = _data["documentLocationName"];
      this.isReturned = _data["isReturned"];
      this.sendTime = _data["sendTime"];
      this.sendUserId = _data["sendUserId"];
      this.sendUserFullName = _data["sendUserFullName"];
      this.expectedReturnTime = _data["expectedReturnTime"];
      this.returnUserId = _data["returnUserId"];
      this.returnUserFullName = _data["returnUserFullName"];
      this.sendDescription = _data["sendDescription"];
      this.returnDescription = _data["returnDescription"];
      this.returnDocumentHealthConfirm = _data["returnDocumentHealthConfirm"];
      this.healthProblemDescription = _data["healthProblemDescription"];
      this.returnTime = _data["returnTime"];
    }
  }

  static fromJS(data: any): UserDocumentTransitionLogDto {
    data = typeof data === "object" ? data : {};
    let result = new UserDocumentTransitionLogDto();
    result.init(data);
    return result;
  }

  toJSON(_data?: any) {
    _data = typeof _data === "object" ? _data : {};
    _data["id"] = this.id;
    _data["userDocumentId"] = this.userDocumentId;
    _data["documentLocationId"] = this.documentLocationId;
    _data["documentLocationName"] = this.documentLocationName;
    _data["isReturned"] = this.isReturned;
    _data["sendTime"] = this.sendTime;
    _data["sendUserId"] = this.sendUserId;
    _data["sendUserFullName"] = this.sendUserFullName;
    _data["expectedReturnTime"] = this.expectedReturnTime;
    _data["returnUserId"] = this.returnUserId;
    _data["returnUserFullName"] = this.returnUserFullName;
    _data["sendDescription"] = this.sendDescription;
    _data["returnDescription"] = this.returnDescription;
    _data["returnDocumentHealthConfirm"] = this.returnDocumentHealthConfirm;
    _data["healthProblemDescription"] = this.healthProblemDescription;
    _data["returnTime"] = this.returnTime;
    return _data;
  }

  clone(): UserDocumentTransitionLogDto {
    const json = this.toJSON();
    let result = new UserDocumentTransitionLogDto();
    result.init(json);
    return result;
  }
}

export interface IUserDocumentTransitionLogDto {
  id: number;
  userDocumentId: number;
  documentLocationId: string;
  documentLocationName: string;
  isReturned: boolean;
  sendTime: string;
  sendUserId: number;
  sendUserFullName: string;
  expectedReturnTime: string;
  returnUserId: number;
  returnUserFullName: string;
  sendDescription: string;
  returnDescription: string;
  returnDocumentHealthConfirm: boolean;
  healthProblemDescription: string;
  files: DocumentFileModel[];
}

export class UserDocumentDto implements IUserDocumentDto {
  id: number;
  fileTypeId: number;
  fileTypeName: string;
  name: string;
  relatedUserId: number;
  relatedUserFullName: string;
  isInOffice: boolean;
  documentLocationId: number;
  documentLocationName: string;
  lastSendTime: string;

  constructor(data?: IUserDocumentDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.fileTypeId = _data["fileTypeId"];
      this.fileTypeName = _data["fileTypeName"];
      this.name = _data["name"];
      this.relatedUserId = _data["relatedUserId"];
      this.relatedUserFullName = _data["relatedUserFullName"];
      this.isInOffice = _data["isInOffice"];
      this.documentLocationId = _data["documentLocationId"];
      this.documentLocationName = _data["documentLocationName"];
      this.lastSendTime = _data["lastSendTime"];
    }
  }

  static fromJS(data: any): UserDocumentDto {
    data = typeof data === "object" ? data : {};
    let result = new UserDocumentDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["fileTypeId"] = this.fileTypeId;
    data["fileTypeName"] = this.fileTypeName;
    data["name"] = this.name;
    data["relatedUserId"] = this.relatedUserId;
    data["relatedUserFullName"] = this.relatedUserFullName;
    data["isInOffice"] = this.isInOffice;
    data["documentLocationId"] = this.documentLocationId;
    data["documentLocationName"] = this.documentLocationName;
    data["lastSendTime"] = this.lastSendTime;
    return data;
  }

  clone(): UserDocumentDto {
    const json = this.toJSON();
    let result = new UserDocumentDto();
    result.init(json);
    return result;
  }
}

export interface IUserDocumentDto {
  id: number;
  fileTypeId: number;
  fileTypeName: string;
  name: string;
  relatedUserId: number;
  relatedUserFullName: string;
  isInOffice: boolean;
  documentLocationId: number;
  documentLocationName: string;
  lastSendTime: string;
}

export class CreateUserDocumentResultDto
  implements ICreateUserDocumentResultDto
{
  id: number;

  constructor(data?: CreateUserDocumentResultDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
    }
  }

  static fromJS(data: any): CreateUserDocumentResultDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateUserDocumentResultDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;

    return data;
  }

  clone(): CreateUserDocumentResultDto {
    const json = this.toJSON();
    let result = new CreateUserDocumentResultDto();
    result.init(json);
    return result;
  }
}

export interface ICreateUserDocumentResultDto {
  id: number;
}

export class CreateUserDocumentDto implements ICreateUserDocumentDto {
  fileTypeId: number;
  fileTypeName: string;
  name: string;
  description: string;
  relatedUserId: number;
  documentLocationId: number;
  files: DocumentFileModel[] = [];

  constructor(data?: ICreateUserDocumentDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.fileTypeId = _data["fileTypeId"];
      this.fileTypeName = _data["fileTypeName"];
      this.relatedUserId = _data["relatedUserId"];
      this.name = _data["name"];
      this.documentLocationId = _data["documentLocationId"];
      this.description = _data["description"];
      if (Array.isArray(_data["files"])) {
        this.files = [] as any;
        for (let item of _data["files"]) this.files.push(item);
      }
    }
  }

  static fromJS(data: any): CreateUserDocumentDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateUserDocumentDto();
    result.init(data);

    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["fileTypeId"] = this.fileTypeId;
    data["fileTypeName"] = this.fileTypeName;
    data["relatedUserId"] = this.relatedUserId;
    data["name"] = this.name;
    data["documentLocationId"] = this.documentLocationId;
    data["description"] = this.description;
    if (Array.isArray(this.files)) {
      data["files"] = [];
      for (let item of this.files) data["files"].push(item);
    }
    return data;
  }

  clone(): CreateUserDocumentDto {
    const json = this.toJSON();
    let result = new CreateUserDocumentDto();
    result.init(json);
    return result;
  }
}

export interface ICreateUserDocumentDto {
  fileTypeId: number;
  fileTypeName: string;
  name: string;
  description: string;
  relatedUserId: number;
  documentLocationId: number;
  files: DocumentFileModel[];
}

export class SendUserDocumentDto implements ISendUserDocumentDto {
  userDocumentIds: number[] = [];
  documentLocationId: number;
  documentSendReasonId: number;
  sendDescription: string;
  expectedReturnDate: string;
  files: DocumentFileModel[] = [];

  constructor(data?: ISendUserDocumentDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.documentLocationId = _data["documentLocationId"];
      this.sendDescription = _data["sendDescription"];
      this.expectedReturnDate = _data["expectedReturnDate"];
      this.documentSendReasonId = _data["documentSendReasonId"];
      if (Array.isArray(_data["userDocumentIds"])) {
        this.userDocumentIds = [] as any;
        for (let item of _data["userDocumentIds"]) this.userDocumentIds.push(item);
      }
      if (Array.isArray(_data["files"])) {
        this.files = [] as any;
        for (let item of _data["files"]) this.files.push(item);
      }
    }
  }

  static fromJS(data: any): SendUserDocumentDto {
    data = typeof data === "object" ? data : {};
    let result = new SendUserDocumentDto();
    result.init(data);

    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["documentLocationId"] = this.documentLocationId;
    data["sendDescription"] = this.sendDescription;
    data["expectedReturnDate"] = this.expectedReturnDate;
    data["documentSendReasonId"] = this.documentSendReasonId;
    if (Array.isArray(this.files)) {
      data["files"] = [];
      for (let item of this.files) data["files"].push(item);
    }
    if (Array.isArray(this.userDocumentIds)) {
      data["userDocumentIds"] = [];
      for (let item of this.userDocumentIds) data["userDocumentIds"].push(item);
    }
    return data;
  }

  clone(): SendUserDocumentDto {
    const json = this.toJSON();
    let result = new SendUserDocumentDto();
    result.init(json);
    return result;
  }
}

export interface ISendUserDocumentDto {
  userDocumentIds: number[];
  documentLocationId: number;
  documentSendReasonId: number;
  sendDescription: string;
  expectedReturnDate: string;
  files: DocumentFileModel[];
}

export class ReturnUserDocumentDto implements IReturnUserDocumentDto {
  userDocumentId: number;
  documentLocationId: number;
  returnDescription: string;
  returnDocumentHealthConfirm: boolean;
  healthProblemDescription: string;
  returnedLinkedDocIds: number[] = [];
  files: DocumentFileModel[] = [];

  constructor(data?: IReturnUserDocumentDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.userDocumentId = _data["userDocumentId"];
      this.documentLocationId = _data["documentLocationId"];
      this.returnDescription = _data["returnDescription"];
      this.returnDocumentHealthConfirm = _data["returnDocumentHealthConfirm"];
      this.healthProblemDescription = _data["healthProblemDescription"];
      this.returnedLinkedDocIds = _data["returnedLinkedDocIds"];
      if (Array.isArray(_data["files"])) {
        this.files = [] as any;
        for (let item of _data["files"]) this.files.push(item);
      }
      if (Array.isArray(_data["returnedLinkedDocIds"])) {
        this.files = [] as any;
        for (let item of _data["returnedLinkedDocIds"]) this.returnedLinkedDocIds.push(item);
      }
    }
  }

  static fromJS(data: any): ReturnUserDocumentDto {
    data = typeof data === "object" ? data : {};
    let result = new ReturnUserDocumentDto();
    result.init(data);

    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["userDocumentId"] = this.userDocumentId;
    data["documentLocationId"] = this.documentLocationId;
    data["returnDescription"] = this.returnDescription;
    data["returnDocumentHealthConfirm"] = this.returnDocumentHealthConfirm;
    data["healthProblemDescription"] = this.healthProblemDescription;
    data["returnedLinkedDocIds"] = this.returnedLinkedDocIds;
    if (Array.isArray(this.files)) {
      data["files"] = [];
      for (let item of this.files) data["files"].push(item);
    }
    if (Array.isArray(this.returnedLinkedDocIds)) {
      data["returnedLinkedDocIds"] = [];
      for (let item of this.returnedLinkedDocIds) data["returnedLinkedDocIds"].push(item);
    }
    return data;
  }

  clone(): ReturnUserDocumentDto {
    const json = this.toJSON();
    let result = new ReturnUserDocumentDto();
    result.init(json);
    return result;
  }
}

export interface IReturnUserDocumentDto {
  userDocumentId: number;
  documentLocationId: number;
  returnDescription: string;
  returnDocumentHealthConfirm: boolean;
  healthProblemDescription: string;
  returnedLinkedDocIds: number[];
  files: DocumentFileModel[];
}

export class DocumentFileModel implements IDocumentFileModel {
  id: number;
  fileBase64: string | ArrayBuffer;
  fileName: string;
  contentType: string;
  title: string;
  relatedToReturn: boolean;
  file: File;
  constructor(data?: IDocumentFileModel) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.fileBase64 = _data["fileBase64"];
      this.fileName = _data["fileName"];
      this.contentType = _data["contentType"];
      this.title = _data["title"];
      this.relatedToReturn = _data["relatedToReturn"];
    }
  }

  static fromJS(data: any): DocumentFileModel {
    data = typeof data === "object" ? data : {};
    let result = new DocumentFileModel();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["fileBase64"] = this.fileBase64;
    data["fileName"] = this.fileName;
    data["contentType"] = this.contentType;
    data["title"] = this.title;
    data["relatedToReturn"] = this.relatedToReturn;
    return data;
  }

  clone(): DocumentFileModel {
    const json = this.toJSON();
    let result = new DocumentFileModel();
    result.init(json);
    return result;
  }
}

export interface IDocumentFileModel {
  id: number;
  fileBase64: string | ArrayBuffer;
  fileName: string;
  contentType: string;
  title: string;
  relatedToReturn: boolean;
}

function blobToText(blob: any): Observable<string> {
  return new Observable<string>((observer: any) => {
    if (!blob) {
      observer.next("");
      observer.complete();
    } else {
      let reader = new FileReader();
      reader.onload = (event) => {
        observer.next((event.target as any).result);
        observer.complete();
      };
      reader.readAsText(blob);
    }
  });
}

export class ApiException extends Error {
  message: string;
  status: number;
  response: string;
  headers: { [key: string]: any };
  result: any;

  constructor(
    message: string,
    status: number,
    response: string,
    headers: { [key: string]: any },
    result: any
  ) {
    super();

    this.message = message;
    this.status = status;
    this.response = response;
    this.headers = headers;
    this.result = result;
  }

  protected isApiException = true;

  static isApiException(obj: any): obj is ApiException {
    return obj.isApiException === true;
  }
}

function throwException(
  message: string,
  status: number,
  response: string,
  headers: { [key: string]: any },
  result?: any
): Observable<any> {
  if (result !== null && result !== undefined) return _observableThrow(result);
  else
    return _observableThrow(
      new ApiException(message, status, response, headers, null)
    );
}
